import { useContext, useEffect, useMemo, useState } from 'react';
import {
  ActivePaymentMethodData,
  AdminPaymentApi,
  PaymentMethodData,
  StripeSessionStatus,
} from '../../../api/apis/AdminPaymentApi';
import { ApiKeys } from '../../../api/apis/ApiKeys';
import { ChargeApi, ChargeData } from '../../../api/apis/ChargeApi';
import { SubscriptionData } from '../../../api/apis/SubscriptionApi';
import { ToastbarContext } from '../../../App';
import { Page, Spacer } from '../../../components';
import { MutationStatus, useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { PaymentCompartments, paymentStorageKey } from '../../../data/payment';
import {
  DeletePaymentMethod,
  EnableAutoPay,
  SetDefaultPaymentMethod,
} from '../../../data/payment/mutations';
import { SubscriptionCompartments, subscriptionStorageKey } from '../../../data/subscription';
import { Toast } from '../../../models/Toast';
import PastInvoiceDrawer from '../past-invoice-drawer/PastInvoiceDrawer';
import { InvoiceCompartments, invoiceStorageKey } from '../../../data/invoices';
import { InvoiceData, InvoiceStatusEnum } from '../../../api/apis/InvoiceApi';
import UnpaidInvoiceCard from '../UnpaidInvoiceCard';
import AutoPayDrawer from '../auto-pay-drawer/AutoPayDrawer';
import { LicenseAgreementData } from '../../../api/apis/LicenseAgreementApi';
import { LicenseCompartments, licenseStorageKey } from '../../../data/license';
import { AdminCompartments, adminStorageKey } from '../../../data/admin';
import { UserData } from '../../../models/UserData';
import {
  CustomerOrganizationCompartments,
  customerOrganizationStorageKey,
} from '../../../data/customerOrganization';
import { CustomerOrganizationData } from '../../../models/CustomerOrganizationData';
import { UserCompartments, userStorageKey } from '../../../data/user';
import { useAdminFilters } from '../../../hooks/useAdminHooks';
import { useLoading } from '../../../hooks/useLoading';
import Invoices from './Invoices';
import PaymentMethods from './PaymentMethods';
import { useCommands } from '../../../helpers/useCommands';
import { GetInvoiceTotal } from '../../../data/invoices/commands/GetInvoiceTotal';
import { BillingAdminManager } from './BillingAdmin';
import { PaidByEnum } from '../../../models/LicenseData';

const Payment = () => {
  const appStorage = useAppStorage();
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const { filterAdmins } = useAdminFilters();
  const commands = useCommands();

  const adminPaymentApi = useService<AdminPaymentApi>(ApiKeys.AdminPayment);
  const chargeApi = useService<ChargeApi>(ApiKeys.Charge);
  const paymentDataCache = appStorage.retrieve<PaymentCompartments>(paymentStorageKey);
  const subscriptionDataCache =
    appStorage.retrieve<SubscriptionCompartments>(subscriptionStorageKey);
  const invoiceDataCache = appStorage.retrieve<InvoiceCompartments>(invoiceStorageKey);
  const customerOrgDataCache = appStorage.retrieve<CustomerOrganizationCompartments>(
    customerOrganizationStorageKey,
  );
  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);
  const adminDataCache = appStorage.retrieve<AdminCompartments>(adminStorageKey);
  const userDataCache = appStorage.retrieve<UserCompartments>(userStorageKey);

  const setDefaultPaymentMethod = useMutation(new SetDefaultPaymentMethod());
  const deletePaymentMethod = useMutation(new DeletePaymentMethod());
  const enableAutoPay = useMutation(new EnableAutoPay());

  const defaultPaymentMethodId = useObservable(
    paymentDataCache.observe$<string>('defaultPaymentMethod'),
  );
  const paymentMethods = useObservable(
    paymentDataCache.observe$<PaymentMethodData[]>('paymentMethods'),
  );
  const stripeSessionStatus = useObservable(
    paymentDataCache.observe$<StripeSessionStatus>('stripeSessionStatus'),
  );
  const customerOrganization = useObservable(
    customerOrgDataCache.observe$<CustomerOrganizationData>('customerOrganization'),
  );
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  const orgAdminsData = useObservable(adminDataCache.observe$<UserData[]>('orgAdmins'));
  const subscriptionData = useObservable(
    subscriptionDataCache.observe$<SubscriptionData[]>('subscription'),
  );
  const orgAdmins = useMemo(() => orgAdminsData ?? [], [orgAdminsData]);
  const subscriptions = useMemo(() => subscriptionData ?? [], [subscriptionData]);

  const paymentHistory = useObservable(paymentDataCache.observe$<ChargeData[]>('paymentHistory'));
  const invoices = useObservable(invoiceDataCache.observe$<InvoiceData[]>('invoices')) ?? [];
  const currentUser = useObservable(userDataCache.observe$<UserData>('user'));

  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [autoPayOpen, setAutoPayOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<ChargeData | undefined>(undefined);
  const [failedInvoices, setFailedInvoices] = useState<SubscriptionData[]>([]);
  const [invoiceTotal, setInvoiceTotal] = useState<number>();
  const [invoiceTotalLoading, setInvoiceTotalLoading] = useState(false);
  const [submittingAutoPay, setSubmittingAutoPay] = useState(false);
  const filteredAdmins = filterAdmins(orgAdmins, currentUser);
  const [activeRow, setActiveRow] = useState<ActivePaymentMethodData | undefined>(undefined);

  const activeOrgPricebooks = useMemo(() => {
    return licenseAgreements?.filter(
      license =>
        license.docStatus && license.pricebookBundleDto.pricebook.paidBy === PaidByEnum.Org,
    );
  }, [licenseAgreements]);

  const defaultPaymentMethod = useMemo(() => {
    return paymentMethods?.find(method => method.id === defaultPaymentMethodId);
  }, [paymentMethods, defaultPaymentMethodId]);

  const autoPay = useMemo(() => {
    return customerOrganization?.automaticPayment;
  }, [customerOrganization]);

  const onViewPastInvoice = (row: { id: number }) => {
    const invoiceData = invoices?.find(invoice => invoice.id === row.id);
    const data = paymentHistory?.find(
      transaction =>
        transaction?.charge?.invoiceId === invoiceData?.invoiceId ||
        transaction?.charge?.invoiceId === invoiceData?.invoiceNumber,
    );
    if (data) {
      setSelectedInvoice(data);
      setPaymentHistoryOpen(true);
    } else {
      setToast(
        new Toast({ message: 'Unable to view invoice details', severity: 'error', open: true }),
      );
    }
  };

  const onSetUpAutoPay = async () => {
    if (paymentMethods && paymentMethods.length > 0) {
      setAutoPayOpen(true);
      if (invoices?.some(invoice => invoice.status === InvoiceStatusEnum.Open)) {
        setInvoiceTotal(undefined);
        setInvoiceTotalLoading(true);
        try {
          const total = await commands.execute(GetInvoiceTotal, {});
          setInvoiceTotal(total);
        } catch {
          setInvoiceTotal(undefined);
          setToast(
            new Toast({
              message: 'Unable to load the outstanding invoice total',
              severity: 'error',
              open: true,
            }),
          );
        } finally {
          setInvoiceTotalLoading(false);
        }
      }
    } else {
      setToast(
        new Toast({
          message: 'Please add a payment method before setting up auto-pay',
          open: true,
          severity: 'error',
        }),
      );
    }
  };

  const onViewReceipt = async (row: { id: number }) => {
    const invoiceData = invoices?.find(invoice => invoice.id === row.id);

    const matchingCharge = paymentHistory?.find(
      payment =>
        payment.charge?.invoiceId === invoiceData?.invoiceId ||
        payment.charge?.invoiceId === invoiceData?.invoiceNumber,
    );

    if (matchingCharge) {
      setLoading(true);
      try {
        const { data: receiptUrl } = await chargeApi.getReceiptUrl(matchingCharge.charge.id);
        window.open(receiptUrl, '_blank');
      } catch {
        setToast(
          new Toast({ message: 'Unable to retrieve receipt', severity: 'error', open: true }),
        );
      } finally {
        setLoading(false);
      }
    } else {
      setToast(new Toast({ message: 'Unable to retrieve receipt', severity: 'error', open: true }));
    }
  };

  const onPayInvoice = (row: { id: number }) => {
    const invoiceData = invoices?.find(invoice => invoice.id === row.id);

    if (invoiceData) {
      window.open(invoiceData.invoiceUrl, '_blank');
    }
  };

  useEffect(() => {
    if (!activeRow) return;

    if (setDefaultPaymentMethod.error) {
      setToast(
        new Toast({
          message: `${activeRow?.name ?? 'Payment method'} could not be set as default`,
          severity: 'error',
          open: true,
        }),
      );
      setActiveRow(undefined);
      return;
    }

    if (setDefaultPaymentMethod.status === MutationStatus.Complete) {
      setToast(
        new Toast({
          message: `${activeRow?.name ?? 'Payment method'} set as default!`,
          severity: 'success',
          open: true,
        }),
      );
      setActiveRow(undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRow?.name, setDefaultPaymentMethod.error, setDefaultPaymentMethod.status]);

  const onSetAsDefault = async (row: any) => {
    setActiveRow(row as ActivePaymentMethodData);
    const newRow = row as PaymentMethodData;
    setDefaultPaymentMethod.action(newRow.id);
  };

  const onAddPayment = async () => {
    setLoading(true);
    try {
      const setupIntentUrl = await adminPaymentApi.setupUrl();
      window.location.replace(setupIntentUrl.data);
    } catch {
      setLoading(false);
      setToast(
        new Toast({
          message: 'Unable to start adding a payment method',
          severity: 'error',
          open: true,
        }),
      );
    }
  };

  const onDelete = async (row: any) => {
    try {
      const newRow = row as PaymentMethodData;
      await deletePaymentMethod.action(newRow.id);
    } catch (err) {
      setToast(
        new Toast({
          message: `${(row as ActivePaymentMethodData).name} could not removed`,
          severity: 'error',
          open: true,
        }),
      );
    }
  };

  const handleSubmit = async () => {
    setSubmittingAutoPay(true);
    try {
      await enableAutoPay.action();
      await customerOrgDataCache.reload('customerOrganization');
      setAutoPayOpen(false);
      setToast(
        new Toast({
          message: 'Auto-pay set up successfully!',
          severity: 'success',
          open: true,
        }),
      );
    } catch {
      setToast(
        new Toast({
          message: 'Unable to set up auto-pay',
          severity: 'error',
          open: true,
        }),
      );
    } finally {
      setSubmittingAutoPay(false);
    }
  };

  useEffect(() => {
    setFailedInvoices(subscriptions.filter(subscription => subscription.paymentFailed));
  }, [subscriptions]);

  return (
    <Page title='Payment'>
      <Spacer height='xxs' />
      <BillingAdminManager admins={filteredAdmins} currentUser={currentUser} />

      <PastInvoiceDrawer
        data={selectedInvoice}
        drawerOpen={paymentHistoryOpen}
        setDrawerOpen={setPaymentHistoryOpen}
      />

      <AutoPayDrawer
        invoices={invoices}
        invoiceTotal={invoiceTotal}
        invoiceTotalLoading={invoiceTotalLoading}
        activeOrgPricebooks={activeOrgPricebooks}
        defaultPaymentMethod={defaultPaymentMethod}
        drawerOpen={autoPayOpen}
        setDrawerOpen={setAutoPayOpen}
        handleSubmit={handleSubmit}
        submitting={submittingAutoPay}
      />
      <Spacer height='xs' />

      {failedInvoices.map(invoice => (
        <>
          <UnpaidInvoiceCard subscription={invoice} />
          <Spacer height='xs' />
        </>
      ))}

      <PaymentMethods
        paymentMethods={paymentMethods}
        defaultPaymentMethodId={defaultPaymentMethodId}
        stripeSessionStatus={stripeSessionStatus}
        onAddPayment={onAddPayment}
        onSetAsDefault={onSetAsDefault}
        onDelete={onDelete}
      />

      <Invoices
        invoices={invoices}
        autoPay={autoPay}
        onSetUpAutoPay={onSetUpAutoPay}
        onViewPastInvoice={onViewPastInvoice}
        onViewReceipt={onViewReceipt}
        onPayInvoice={onPayInvoice}
      />
    </Page>
  );
};

export default Payment;
